import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { jobsEndpoint } from "../constants";

const EditOffers = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const token = localStorage.getItem("token");

  // Form state
  const [formData, setFormData] = useState({
    job: "",
    service: "",
    recipient: "",
    income: "",
    contract_type: "PERMANENT",
    location_type: "OFFICE",
    bonus: "",
    expire_date: "",
    notes: "",
    status: "DRAFT"
  });

  // Dropdown options
  const [jobs, setJobs] = useState([]);
  const [applicants, setApplicants] = useState([]);

  // Contract type options
  const contractTypes = [
    { value: "PERMANENT", label: "Permanent" },
    { value: "CONTRACT", label: "Contract" },
    { value: "TEMPORARY", label: "Temporary" },
    { value: "FREELANCE", label: "Freelance" },
    { value: "INTERNSHIP", label: "Internship" }
  ];

  // Location type options
  const locationTypes = [
    { value: "OFFICE", label: "Office" },
    { value: "REMOTE", label: "Remote" },
    { value: "HYBRID", label: "Hybrid" },
    { value: "ON_SITE", label: "On-site" }
  ];

  // Status options
  const statusOptions = [
    { value: "DRAFT", label: "Draft" },
    { value: "PENDING", label: "Pending" },
    { value: "ACCEPTED", label: "Accepted" },
    { value: "DECLINED", label: "Declined" },
    { value: "WITHDRAWN", label: "Withdrawn" },
    { value: "CANCELLED", label: "Cancelled" }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch the offer details
        const offerResponse = await axios.get(`${jobsEndpoint}${offerId}/offer_detail/`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          }
        });

        const offer = offerResponse.data;
        
        // Set form data with offer details
        setFormData({
          job: offer.job?.id || "",
          service: offer.service?.id || "",
          recipient: offer.recipient?.id || "",
          income: offer.income || "",
          contract_type: offer.contract_type || "PERMANENT",
          location_type: offer.location_type || "OFFICE",
          bonus: offer.bonus || "",
          expire_date: offer.expire_date ? new Date(offer.expire_date).toISOString().split('T')[0] : "",
          notes: offer.notes || "",
          status: offer.status || "DRAFT"
        });

        // Fetch dropdown options
        const [jobsResponse, applicantsResponse] = await Promise.all([
          axios.get(`${jobsEndpoint}my_jobs/`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Token ${token}`,
            }
          }),

          axios.get(`${jobsEndpoint}all_applicants/`, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Token ${token}`,
            }
          })
        ]);

        setJobs(jobsResponse.data.results || jobsResponse.data.jobs || []);
        setApplicants(applicantsResponse.data.results || []);

      } catch (err) {
        setError("Failed to load offer details.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (offerId) {
      fetchData();
    }
  }, [offerId, token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const submitData = {
        ...formData,
        income: formData.income ? parseInt(formData.income) : null,
        expire_date: formData.expire_date ? new Date(formData.expire_date).toISOString() : null
      };

      await axios.put(`${jobsEndpoint}${offerId}/update_offers/`, submitData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        }
      });

      setSuccess("Offer updated successfully!");
      setTimeout(() => {
        navigate("/client-offers");
      }, 1500);

    } catch (err) {
      setError("Failed to update offer. Please try again.");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/client-offers");
  };

  if (loading) {
    return (
      <div className="lg:p-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading offer details...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <span 
            onClick={() => navigate("/jobs")}
            className="hover:text-gray-700 cursor-pointer"
          >
            Jobs
          </span>
          <span>›</span>
          <span 
            onClick={() => navigate("/client-offers")}
            className="hover:text-gray-700 cursor-pointer"
          >
            Offers
          </span>
          <span>›</span>
          <span className="text-gray-900">Edit Offer</span>
        </div>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Edit Offer</h1>
            <p className="mt-1 text-sm text-gray-600">
              Update the details of your job offer.
            </p>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <div className="text-green-600 mr-3">✓</div>
            <div className="text-green-700">{success}</div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <div className="text-red-600 mr-3">✗</div>
            <div className="text-red-700">{error}</div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Job and Service Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Position *
              </label>
              <select
                name="job"
                value={formData.job}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
              >
                <option value="">Select a job</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>
                    {job.title} - {job.company}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service
              </label>
              <select
                name="service"
                value={formData.service}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
              >
               
              </select>
            </div>
          </div>

          {/* Recipient */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient (Candidate) *
              </label>
              <select
                name="recipient"
                value={formData.recipient}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
              >
                <option value="">Select a candidate</option>
                {applicants.map(applicant => (
                  <option key={applicant.user.id} value={applicant.user.id}>
                    {applicant.user.first_name} {applicant.user.last_name} - {applicant.user.email}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Income and Contract Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Salary/Income (£) *
              </label>
              <input
                type="number"
                name="income"
                value={formData.income}
                onChange={handleInputChange}
                required
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
                placeholder="Enter salary amount"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contract Type *
              </label>
              <select
                name="contract_type"
                value={formData.contract_type}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
              >
                {contractTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location Type *
              </label>
              <select
                name="location_type"
                value={formData.location_type}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
              >
                {locationTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Bonus and Expiry Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bonus/Benefits
              </label>
              <input
                type="text"
                name="bonus"
                value={formData.bonus}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
                placeholder="Enter bonus details or benefits"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiry Date
              </label>
              <input
                type="date"
                name="expire_date"
                value={formData.expire_date}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
              />
            </div>
          </div>

          {/* Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status *
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
              >
                {statusOptions.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows="4"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
              placeholder="Add any additional notes or comments about this offer..."
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-[#19bfb7] text-white rounded-lg hover:bg-[#17a8a0] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Updating..." : "Update Offer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditOffers;